import { ReplayTransport, type ReplaySpeed, type TlogRecord } from '@hgc/core';
import {
  BluetoothTransport,
  DEFAULT_BAUD_RATE,
  SerialTransport,
  SimulatedTransport,
  WebSocketTransport,
  type Transport,
} from '@hgc/link';

/**
 * "要用哪条链路"的完整描述。
 *
 * 四种真实传输 + 内置模拟机 + 回放，全部收在一个**可序列化的判别联合**里：
 * 它既是 `LinkManager.connect()` 的入参，也是将来 `app-connect` 连接页要保存/恢复的对象
 * （写进 localStorage、或从命令行参数还原）。因此这里不出现任何构造好的实例，只出现"要连什么"。
 *
 * 回放的 `records` 是唯一携带数据的成员：`.tlog` 一旦读进来就必须整体交给传输层，
 * 而传输层对上层仍是普通的 `Transport` —— 回放因此与真机走同一条解析与归集路径，
 * 复盘结论才可信（细节见 `packages/core/src/tlog/replay-transport.ts`）。
 */
export type LinkConfig =
  | {
      readonly kind: 'simulated';
      /** 机体数量，默认 1。多机时系统 ID 从 1 开始递增 */
      readonly count?: number;
      readonly aircraft?: 'copter' | 'plane';
      readonly tickMs?: number;
    }
  | { readonly kind: 'websocket'; readonly url: string; readonly protocols?: readonly string[] }
  | { readonly kind: 'serial'; readonly baudRate?: number }
  | { readonly kind: 'bluetooth' }
  | {
      readonly kind: 'replay';
      readonly records: readonly TlogRecord[];
      readonly label?: string;
      readonly speed?: ReplaySpeed;
    };

/** 开发默认：启动就连内置模拟机，没有真机也能把整条链路和界面走通 */
export const DEFAULT_LINK_CONFIG: LinkConfig = Object.freeze({ kind: 'simulated' });

/**
 * 用配置造一个传输实例。可能抛错（例如 WebSocket 地址非法），调用方需要处理 ——
 * 这类错误必须在**连接之前**就显示出来，而不是等到超时。
 */
export function createTransport(config: LinkConfig): Transport {
  switch (config.kind) {
    case 'simulated':
      return new SimulatedTransport({
        // `exactOptionalPropertyTypes` 下不能显式传 undefined，只能条件展开
        ...(config.count === undefined ? {} : { count: config.count }),
        ...(config.aircraft === undefined ? {} : { kind: config.aircraft }),
        ...(config.tickMs === undefined ? {} : { tickMs: config.tickMs }),
      });
    case 'websocket':
      return new WebSocketTransport({
        url: config.url,
        ...(config.protocols === undefined ? {} : { protocols: config.protocols }),
      });
    case 'serial':
      return new SerialTransport(config.baudRate === undefined ? {} : { baudRate: config.baudRate });
    case 'bluetooth':
      return new BluetoothTransport();
    case 'replay':
      return new ReplayTransport(config.records, {
        ...(config.label === undefined ? {} : { label: config.label }),
        ...(config.speed === undefined ? {} : { speed: config.speed }),
      });
  }
}

/**
 * 用一句话说明"要连的是什么"。
 *
 * 传输实例还没建出来（或建失败）时也要能显示，所以它不能依赖 `Transport.label`。
 */
export function describeLinkConfig(config: LinkConfig): string {
  switch (config.kind) {
    case 'simulated':
      return config.count === undefined || config.count <= 1
        ? `内置模拟机（${config.aircraft === 'plane' ? '固定翼' : '多旋翼'}）`
        : `内置模拟机（${config.count} 架）`;
    case 'websocket':
      return `WebSocket ${config.url}`;
    case 'serial':
      return `串口 @ ${config.baudRate ?? DEFAULT_BAUD_RATE}`;
    case 'bluetooth':
      return '蓝牙（Nordic UART 透传）';
    case 'replay':
      return `回放 ${config.label ?? '遥测记录'}`;
  }
}
